import { gql, useQuery } from '@apollo/client';
import React from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';

import { GraphRef } from '../../hooks/useGraphRef';
import {
  CheckConfigFragment,
  checksConfigPageStatsQuery,
} from '../checksConfigPage/ChecksConfigPage';
import {
  STATS_TIME_RANGE,
  checksTimeRangeInSeconds,
} from '../checksConfigPage/constants';

import { buildStatsMap, sortByStatsData } from './common/helpers';
import { DowngradeChecksCard } from './downgradeChecks/DowngradeChecksCard';
import { BlockingDownstreamVariantsCard } from './downstreamVariants/BlockingDownstreamVariantsCard';
import { ExcludedClientsCard } from './excludedClients/ExcludedClientsCard';
import { ExcludedOperationsCard } from './excludedOperations/ExcludedOperationsCard';
import { IncludedVariantsCard } from './includedVariants/IncludedVariantsCard';
import { OperationsCheckCard } from './operationsCheckSetting/OperationsCheckCard';
import { TimeRangeCard } from './timeRange/TimeRangeCard';
import { VariantCheckConfigFragment } from './VariantCheckConfigFragment';

const variantChecksConfigPageQuery = gql`
  query VariantChecksConfigPageQuery($graphId: ID!, $graphVariant: String!) {
    service(id: $graphId) {
      id
      roles {
        canWriteCheckConfiguration
      }
      variants {
        id
        name
      }
      checkConfiguration {
        id
        ...CheckConfigFragment
      }
      variant(name: $graphVariant) {
        id
        isContract
        checkConfiguration {
          id
          ...VariantCheckConfigFragment
        }
        downstreamVariants: derivedVariants {
          id
          name
        }
      }
    }
  }
  ${CheckConfigFragment}
  ${VariantCheckConfigFragment}
`;

export const VariantChecksConfigPage = ({
  graphRef,
}: {
  graphRef: GraphRef;
}) => {
  const shouldShowDowngradeChecksConfig = useLDFlag(
    'pulsar-show-downgrade-checks-config',
  );

  const { data, loading, error } = useQuery<
    GraphQLTypes.VariantChecksConfigPageQuery,
    GraphQLTypes.VariantChecksConfigPageQueryVariables
  >(variantChecksConfigPageQuery, {
    variables: {
      graphId: graphRef.graphId,
      graphVariant: graphRef.graphVariant,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    accountId: currentAccountId as string,
    skip: !currentAccountId,
  });

  const { data: statsData, loading: statsLoading } = useQuery<
    GraphQLTypes.ChecksConfigPageStatsQuery,
    GraphQLTypes.ChecksConfigPageStatsQueryVariables
  >(checksConfigPageStatsQuery, {
    variables: {
      graphId: graphRef.graphId,
      timeFrom: `-${checksTimeRangeInSeconds[STATS_TIME_RANGE]}`,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const graphCheckConfig = data?.service?.checkConfiguration;
  const variantCheckConfig = data?.service?.variant?.checkConfiguration;

  if (loading || currentPlan.loading) {
    return <Loading />;
  }

  if (error || currentPlan.error) {
    throw error || currentPlan.error;
  }

  if (!data?.service?.variant) {
    return <NotFound />;
  }

  if (!graphCheckConfig || !variantCheckConfig) {
    return <NotFound />;
  }

  // TODO: don't query this
  const roles = data.service.roles ?? {
    __typename: 'ServiceRoles',
    canWriteCheckConfiguration: false,
  };

  const allVariants = sortByStatsData(
    data.service.variants.map(({ name }) => name) ?? [],
    statsData?.service?.operationCheckRequestsByVariant,
  );

  const variantStatsMap = buildStatsMap(
    statsData?.service?.operationCheckRequestsByVariant,
  );

  const unsortedDownstreamVariants =
    data.service.variant.downstreamVariants?.map(({ name }) => name) ?? [];

  const downstreamVariants = sortByStatsData(
    unsortedDownstreamVariants,
    statsData?.service?.operationCheckRequestsByVariant,
  ).filter((variantName) => unsortedDownstreamVariants.includes(variantName));

  return (
    <>
      <OperationsCheckCard
        graphRef={graphRef}
        variantCheckConfig={variantCheckConfig}
        roles={roles}
      />
      {variantCheckConfig.operationsChecksEnabled && (
        <>
          <TimeRangeCard
            graphRef={graphRef}
            graphCheckConfig={graphCheckConfig}
            variantCheckConfig={variantCheckConfig}
            roles={roles}
          />
          {data.service.variant.isContract === false &&
            downstreamVariants.length > 0 && (
              <BlockingDownstreamVariantsCard
                graphRef={graphRef}
                graphCheckConfig={graphCheckConfig}
                variantCheckConfig={variantCheckConfig}
                downstreamVariants={downstreamVariants}
                statsLoading={statsLoading}
                statsData={variantStatsMap}
                roles={roles}
              />
            )}
          <IncludedVariantsCard
            graphRef={graphRef}
            graphCheckConfig={graphCheckConfig}
            variantCheckConfig={variantCheckConfig}
            allVariants={allVariants}
            statsLoading={statsLoading}
            statsData={variantStatsMap}
            roles={roles}
          />
          <ExcludedClientsCard
            graphRef={graphRef}
            graphCheckConfig={graphCheckConfig}
            variantCheckConfig={variantCheckConfig}
            roles={roles}
            canViewClientVersions={
              currentPlan.currentPlan?.clientVersions ?? false
            }
          />
          <ExcludedOperationsCard
            graphRef={graphRef}
            graphCheckConfig={graphCheckConfig}
            variantCheckConfig={variantCheckConfig}
            roles={roles}
          />
          {shouldShowDowngradeChecksConfig && (
            <DowngradeChecksCard
              graphRef={graphRef}
              graphCheckConfig={graphCheckConfig}
              variantCheckConfig={variantCheckConfig}
              roles={roles}
            />
          )}
        </>
      )}
    </>
  );
};
